import React from 'react';

/**
 * SimpleMarkdown — lightweight, dependency-free renderer for the subset of
 * Markdown used in lesson content (DriveMaster). Handles:
 *   ## Heading 2     →  <h2>
 *   ### Heading 3    →  <h3>
 *   **bold**         →  <strong>
 *   *italic*         →  <em>
 *   `code`           →  <code>
 *   - item / • item  →  <ul><li>
 *   1. item          →  <ol><li>
 *   blank line       →  paragraph break
 *
 * Why not react-markdown? Lessons content is short, controlled by admins, and
 * the markdown vocabulary is tiny. Avoiding the dep saves ~80 KB bundle.
 *
 * Safety: All segments are rendered as React children (escaped). Only the
 * explicit inline tags above are translated. No raw HTML, no script injection.
 */
const escapeForRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const renderInline = (text, keyPrefix = '') => {
  const parts = [];
  let remaining = text;
  let i = 0;

  while (remaining.length > 0) {
    // Match **bold**, *italic*, or `code` — first wins
    const m =
      remaining.match(/^([\s\S]*?)\*\*([^*\n]+)\*\*/) ||
      remaining.match(/^([\s\S]*?)`([^`\n]+)`/) ||
      remaining.match(/^([\s\S]*?)\*([^*\n]+)\*/);

    if (!m) {
      parts.push(remaining);
      break;
    }

    const [whole, before, inner] = m;
    if (before) parts.push(before);
    const marker = whole.slice(before.length, before.length + 2);
    if (marker.startsWith('**')) {
      parts.push(<strong key={`${keyPrefix}-b-${i}`}>{inner}</strong>);
    } else if (marker.startsWith('`')) {
      parts.push(
        <code key={`${keyPrefix}-c-${i}`} className="px-1 py-0.5 rounded bg-slate-100 text-slate-900 text-[0.9em]">
          {inner}
        </code>
      );
    } else {
      parts.push(<em key={`${keyPrefix}-i-${i}`}>{inner}</em>);
    }
    remaining = remaining.slice(whole.length);
    i++;
  }
  return parts;
};

const SimpleMarkdown = ({ source = '', className = '' }) => {
  if (!source || typeof source !== 'string') return null;

  // Normalize Windows line endings and common bullet characters.
  const text = source.replace(/\r\n/g, '\n');
  const lines = text.split('\n');

  const blocks = [];
  let buffer = []; // lines belonging to the current paragraph
  let listBuffer = []; // items in the current list
  let listOrdered = false;
  let blockKey = 0;

  const flushParagraph = () => {
    if (buffer.length === 0) return;
    const para = buffer.join(' ').trim();
    if (para.length > 0) {
      blocks.push(
        <p key={`p-${blockKey++}`} className="mb-3 last:mb-0 leading-relaxed">
          {renderInline(para, `p${blockKey}`)}
        </p>
      );
    }
    buffer = [];
  };

  const flushList = () => {
    if (listBuffer.length === 0) return;
    const Tag = listOrdered ? 'ol' : 'ul';
    blocks.push(
      <Tag
        key={`l-${blockKey++}`}
        className={`${listOrdered ? 'list-decimal' : 'list-disc'} ps-6 mb-3 space-y-1 leading-relaxed`}
      >
        {listBuffer.map((item, idx) => (
          <li key={`li-${idx}`}>{renderInline(item, `li${blockKey}-${idx}`)}</li>
        ))}
      </Tag>
    );
    listBuffer = [];
  };

  const flushAll = () => {
    flushParagraph();
    flushList();
  };

  for (let raw of lines) {
    const line = raw.trim();

    // Blank line — paragraph break
    if (line === '') {
      flushAll();
      continue;
    }

    // Headings
    const h3 = line.match(/^###\s+(.+)$/);
    const h2 = line.match(/^##\s+(.+)$/);
    if (h3) {
      flushAll();
      blocks.push(
        <h3 key={`h-${blockKey++}`} className="text-lg sm:text-xl font-bold text-slate-900 mt-5 mb-2">
          {renderInline(h3[1], `h3${blockKey}`)}
        </h3>
      );
      continue;
    }
    if (h2) {
      flushAll();
      blocks.push(
        <h2 key={`h-${blockKey++}`} className="text-xl sm:text-2xl font-bold text-slate-900 mt-6 mb-3">
          {renderInline(h2[1], `h2${blockKey}`)}
        </h2>
      );
      continue;
    }

    // Ordered list  "1. item"
    const ol = line.match(/^(\d+)\.\s+(.+)$/);
    if (ol) {
      flushParagraph();
      if (!listOrdered && listBuffer.length > 0) flushList();
      listOrdered = true;
      listBuffer.push(ol[2]);
      continue;
    }

    // Unordered list  "- item"  or  "* item"  or  "• item"
    const ul = line.match(/^[-*•]\s+(.+)$/);
    if (ul) {
      flushParagraph();
      if (listOrdered && listBuffer.length > 0) flushList();
      listOrdered = false;
      listBuffer.push(ul[1]);
      continue;
    }

    // Regular paragraph line
    if (listBuffer.length > 0) flushList();
    buffer.push(line);
  }

  flushAll();

  return <div className={className}>{blocks}</div>;
};

export default SimpleMarkdown;
